import { useUsersApi } from "@/Hooks/useUsersApi";
import type { DialogBaseProps } from "@freightsimple/ui-core/Dialog/DialogBaseProps";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { CopyToClipboardButton } from "@freightsimple/ui-core/Buttons/CopyToClipboardButton";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Spinner } from "@freightsimple/ui-core/Feedback/Spinner";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { CopyableLinkBox } from "@/Shared/Components/CustomComponents/CopyableLinkBox";
import { rootUrl } from "@/Environment";
import { useEffect, useState } from "react";

interface ReferralCodeDialogProps extends DialogBaseProps {
  /** Supplies the code directly instead of fetching it — keeps stories
   * deterministic (a live fetch's rejection logs at unpredictable times). */
  referralCode?: string | undefined;
}

// Give $100 / get $100: shows the customer's personal referral link with a
// copy-to-clipboard affordance. Replaces the legacy imperative Cargo modal.
export function ReferralCodeDialog(props: ReferralCodeDialogProps) {
  const usersApi = useUsersApi();
  const [fetchedCode, setFetchedCode] = useState<string | undefined>();
  const referralCode = props.referralCode ?? fetchedCode;

  useEffect(() => {
    if (!props.open || referralCode !== undefined) {
      return;
    }
    usersApi.getReferralCode().then(
      (response) => setFetchedCode(response.referralCode),
      (e) => console.error(e),
    );
  }, [props.open]);

  const url = `${rootUrl()}referral?code=${referralCode}`;

  return (
    <Dialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      title="Referral Code"
      width="wide"
      footer={
        <Button primary onClick={() => props.onOpenChange?.(false)}>
          Done
        </Button>
      }
    >
      <ControlPresentationProvider presentation="underlined">
        {!referralCode && <Spinner size={32} />}
        {referralCode && (
          <Stack gap={4} align="start">
            {/* `secondary`, not `muted`: this is the body copy that explains
                the offer. Muted is for units and hints, and at this length it
                read as greyed-out. */}
            <Stack gap={2} align="start">
              <T size="base" color="secondary">
                Give $100 and get $100 giftcard for yourself. If you love the
                FreightSimple experience as much as we do, perhaps you would
                like to share it with your contacts. We'll give them $100 off
                their first shipment, and when they book you'll get a $100 gift
                card yourself.
              </T>
              <T size="base" color="secondary">
                You can copy this link and send it to them.
              </T>
            </Stack>
            <Row gap={3} shrinkable>
              <CopyableLinkBox url={url} />
              <CopyToClipboardButton
                data={url}
                label="Referral link"
                size="link"
              >
                Click here to copy to clipboard
              </CopyToClipboardButton>
            </Row>
          </Stack>
        )}
      </ControlPresentationProvider>
    </Dialog>
  );
}
